import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { AlarmService } from '../../../services/alarm.service';
import { ChartPieComponent } from '../../../shared/components/pie-chart/pie-chart';
import { getAlarmTypeColor } from '../../../shared/utils/map/map-marker.util';
import { AlarmDto } from '../../../types/dtos/alarm.dto';
import { AlarmType } from '../../../types/enums/alarm.enum';
import {TranslateSafePipe} from '../../../shared/pipes/translation.pipe';
import { PyChart, PyChartData } from '../../../types/interfaces/charts.interface';

@Component({
  selector: 'app-alarms-pie-chart',
  standalone: true,
  imports: [
    ChartPieComponent,
    TranslateSafePipe,
  ],
  templateUrl: './alarms-pie-chart.html',
  styleUrl: './alarms-pie-chart.css',
})
export class AlarmsPieChart implements OnInit {
  public pyChartData: PyChart = {
    data: [],
  }

  private readonly ALARM_TYPE_ORDER: AlarmType[] = [
    AlarmType.LEAK,
    AlarmType.LOW_BATTERY,
    AlarmType.MANIPULATION,
    AlarmType.DRY,
    AlarmType.REVERSE_FLOW,
    AlarmType.NO_DATA,
    AlarmType.OFFLINE,
  ];

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _alarmService: AlarmService,
    private readonly _translateService: TranslateService,
  ) {
  }

  public ngOnInit(): void {
    this._alarmService.unResolvedAlarms$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((alarms: AlarmDto[]) => {
        this.setPieChartData(alarms);
      });
  }

  private setPieChartData(alarms: AlarmDto[]): void {
    const alarmTypeCountMap: Map<AlarmType, number> = new Map<AlarmType, number>();

    alarms.forEach((alarm: AlarmDto) => {
      alarmTypeCountMap.set(
        alarm.alarm_type,
        (alarmTypeCountMap.get(alarm.alarm_type) ?? 0) + 1,
      );
    });

    const sortedAlarmTypes: AlarmType[] = [...alarmTypeCountMap.keys()]
      .sort((firstType: AlarmType, secondType: AlarmType) => {
        const firstIndex: number = this.ALARM_TYPE_ORDER.indexOf(firstType);
        const secondIndex: number = this.ALARM_TYPE_ORDER.indexOf(secondType);

        return firstIndex - secondIndex;
      });

    this.pyChartData.data = sortedAlarmTypes.map((alarmType: AlarmType) => {
      return {
        label: this._translateService.instant('common.enum.alarm-type.' + alarmType),
        value: alarmTypeCountMap.get(alarmType) ?? 0,
        key: alarmType,
        color: getAlarmTypeColor(alarmType)
      }
    });
  }
}
